/**
 * Phase 45 (2026-07-28) - Radial Progress Ring for Total Expenses MTD vs budget.
 *
 * Pure SVG. Apple-Fitness-style ring:
 *   - 80px outer diameter, 8px thickness.
 *   - Track (unfilled) = --border-default at 30% opacity.
 *   - Arc color based on pct_used bucket via `arcColorHint`:
 *       success (< 60%), warning (60-84%), danger (>= 85%).
 *   - Starts at 12 o'clock; fills clockwise.
 *   - Center overlay: big % + tiny uppercase "OF BUDGET".
 *
 * Props: { pct, arcColorHint, size=80, thickness=8, label="of budget" }.
 */

const HINT_TO_VAR = {
  success: "var(--success)",
  warning: "#F97316", // orange-amber per spec (kept literal - not swapped per theme)
  danger:  "var(--danger)",
};

export default function RadialProgressRing({
  pct,
  arcColorHint = "success",
  size = 80,
  thickness = 8,
  label = "of budget",
}) {
  const clampedPct = Math.max(0, Math.min(100, Number(pct || 0)));
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;
  const circumference = 2 * Math.PI * r;
  const arcLength = (clampedPct / 100) * circumference;
  const arcColor = HINT_TO_VAR[arcColorHint] || HINT_TO_VAR.success;

  return (
    <div
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
      data-testid="expenses-ring"
      data-pct={clampedPct.toFixed(1)}
      data-arc-color={arcColorHint}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--border-default)"
          strokeOpacity={0.55}
          strokeWidth={thickness}
        />
        {/* Filled arc */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={arcColor}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${arcLength} ${circumference - arcLength}`}
          strokeDashoffset={0}
        />
      </svg>
      {/* Center overlay */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center leading-none"
        data-testid="expenses-ring-center"
      >
        <span
          className="text-[18px] font-semibold tabular-nums"
          style={{ color: "var(--text-primary)" }}
        >
          {Math.round(clampedPct)}%
        </span>
        <span
          className="mt-0.5 text-[8px] tracking-[0.14em] uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
